import { Images, Settings } from 'lucide-react';

interface GalleryPermissionDialogProps {
  /** 'denied' = can re-ask; 'permanently-denied' = must open Settings */
  type: 'denied' | 'permanently-denied';
  onRetry: () => void;
  onDismiss: () => void;
}

/**
 * Modal shown when the Android gallery permission has been denied.
 *
 *  - `denied`            → explains why access is needed and offers "Allow Access"
 *  - `permanently-denied` → guides the user to enable it manually in Settings
 */
export default function GalleryPermissionDialog({
  type,
  onRetry,
  onDismiss,
}: GalleryPermissionDialogProps) {
  const isPermanent = type === 'permanently-denied';

  return (
    <dialog
      open
      aria-labelledby="gallery-perm-title"
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[9999] px-4 pb-4 sm:pb-0 m-0 max-w-none w-full h-full border-0 bg-transparent p-0"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm animate-slide-up p-6">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
            {isPermanent ? (
              <Settings className="w-8 h-8 text-brand-primary" />
            ) : (
              <Images className="w-8 h-8 text-brand-primary" />
            )}
          </div>
        </div>

        {/* Title */}
        <h2
          id="gallery-perm-title"
          className="text-lg font-semibold text-gray-800 text-center mb-2"
        >
          {isPermanent ? 'Permission Required' : 'Allow Photo Access'}
        </h2>

        {/* Body */}
        {isPermanent ? (
          <div className="text-sm text-gray-500 text-center mb-6 space-y-1">
            <p>Photo access was permanently denied.</p>
            <p>To enable it, open your device settings:</p>
            <p className="font-medium text-gray-700 mt-2">
              Settings → Apps → ISentryx → Permissions → Photos / Files and Media
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center mb-6">
            ISentryx needs access to your photos to upload a profile picture.
            Please allow access when prompted.
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {!isPermanent && (
            <button
              onClick={onRetry}
              className="w-full bg-brand-primary hover:bg-brand-hover active:bg-brand-active text-white font-medium py-3 rounded-lg transition-colors"
            >
              Allow Access
            </button>
          )}
          <button
            onClick={onDismiss}
            className="w-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 font-medium py-3 rounded-lg transition-colors"
          >
            {isPermanent ? 'OK' : 'Not Now'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
